"use client";

import type { Button as GeneratedButtonProps } from "@homeinstead/schemas";

import { track } from "@homeinstead/analytics";
import { EventName } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import { Button as UIButton } from "@homeinstead/ui/components/Button";
import Link from "next/link";
import {
  COLOR_VARIANT_MAP,
  FONT_WEIGHT_CLASS,
  WIDTH_VARIANT_CLASS,
} from "~www/lib/button-style-classes";

type ButtonContentProps = Omit<GeneratedButtonProps, "_type">;

const ALIGNMENT_CLASS: Record<string, string> = {
  center: "justify-center",
  left: "justify-start",
  right: "justify-end",
};

function isSamePageHashHref(href: string): boolean {
  return href.startsWith("#") && href.length > 1;
}

function ButtonContent({
  label,
  href,
  colorVariant,
  widthVariant,
  fontWeight,
  contentAlignment,
}: ButtonContentProps) {
  if (!label || !href) {
    return null;
  }

  const useNativeAnchor = isSamePageHashHref(href);

  const alignmentClass =
    contentAlignment && ALIGNMENT_CLASS[contentAlignment]
      ? ALIGNMENT_CLASS[contentAlignment]
      : ALIGNMENT_CLASS["left"];

  function handleClick() {
    track(EventName.TRACKED_CLICK, {
      element_name: "page_builder_button",
      link_title: label,
      target_url: href,
    });
  }

  return (
    <div className={cn("flex", alignmentClass)}>
      <UIButton
        asChild
        className={cn({
          [WIDTH_VARIANT_CLASS[widthVariant]]: widthVariant,
          [WIDTH_VARIANT_CLASS["auto"]]: !widthVariant,
          [FONT_WEIGHT_CLASS[fontWeight]]: fontWeight,
        })}
        size="lg"
        variant={colorVariant ? COLOR_VARIANT_MAP[colorVariant] : undefined}
      >
        {useNativeAnchor ? (
          <a href={href} onClick={handleClick}>
            {label}
          </a>
        ) : (
          <Link href={href} onClick={handleClick}>
            {label}
          </Link>
        )}
      </UIButton>
    </div>
  );
}

export { ButtonContent };
